import React, { useState, useEffect, useRef } from "react";
import useAxios from "../../utils/useAxios";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { SensorOccupied } from "@mui/icons-material";
import { useToast } from "@chakra-ui/react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Modal,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Image, Upload } from "antd";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import { MAX_GALLERY_IMAGES } from "../../config/uploadLimits";
import {
  createLimitedBeforeUpload,
  getImageUploadLimitState,
} from "../../utils/imageUploadLimit";
import EquipmentAutocomplete from "../Assets/EquipmentAutocomplete";
import TierAutocomplete from "../Assets/TierAutocomplete";
import PanelComponentAutocomplete from "../Assets/PanelComponentAutocomplete";

const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const validationSchema = Yup.object({
  tagNumber: Yup.string().required("Tag number is required"),
  instrumentType: Yup.number().required("Instrument type is required"),
  communicationProtocol: Yup.number().nullable(),
  description: Yup.string(),
  plc: Yup.number().nullable(),
  pid: Yup.number().nullable(),
  ioPanel: Yup.number().nullable(),
  card: Yup.string(),
  channel: Yup.string(),
});

const NewInstrument = ({ reloadComponents }) => {
  const api = useAxios();
  // Route: /sites/:siteId/plants/:plantId/assets/:assetId/sub-asset/:subAssetId/components/:componentId
  const { siteId, plantId, assetId, subAssetId, componentId } = useParams();
  const toast = useToast();

  const [open, setOpen] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");

  // Fetched from API
  const [instrumentTypes, setInstrumentTypes] = useState([]);
  const [commProtocols, setCommProtocols] = useState([]);
  // plc + pid share the same Equipment endpoint (filtered by plant + "AS")
  const [plcOptions, setPlcOptions] = useState([]);
  // pid options: all equipment filtered by plant
  const [pidOptions, setPidOptions] = useState([]);
  // ioPanel: tiers filtered by equipment (assetId)
  const [ioPanelOptions, setIoPanelOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // "Parent Assets"-style cross-links (relatedEquipment/relatedTiers/
  // relatedComponents) - same concept as EditTier/EditComponent's "Parent
  // Assets" picker, at all three levels an Instrument can relate to. Kept
  // outside Formik (full objects, for the async pickers to display),
  // mirroring how fileList already sits outside Formik state in this file;
  // ids are read straight from these in handleSubmit.
  const [relatedEquipment, setRelatedEquipment] = useState([]);
  const [relatedTiers, setRelatedTiers] = useState([]);
  const [relatedComponents, setRelatedComponents] = useState([]);

  const apiRef = useRef(api);
  apiRef.current = api;

  // Pre-seed the related pickers from the current route context (the
  // equipment/tier/component this instrument is being created under),
  // matching NewTier's/NewComponent's own relatedTo pre-seeding - still
  // fully editable/removable afterwards.
  useEffect(() => {
    let cancelled = false;

    const preseed = async () => {
      const [equipmentRes, tierRes, componentRes] = await Promise.all([
        assetId ? apiRef.current.get(`/equipment/${assetId}/`).catch(() => null) : Promise.resolve(null),
        subAssetId ? apiRef.current.get(`/tiers/${subAssetId}/`).catch(() => null) : Promise.resolve(null),
        componentId
          ? apiRef.current.get(`/panel-components/${componentId}/`).catch(() => null)
          : Promise.resolve(null),
      ]);
      if (cancelled) return;
      if (equipmentRes?.data) setRelatedEquipment([equipmentRes.data]);
      if (tierRes?.data) setRelatedTiers([tierRes.data]);
      if (componentRes?.data) setRelatedComponents([componentRes.data]);
    };

    preseed();
    return () => {
      cancelled = true;
    };
  }, [assetId, subAssetId, componentId]);

  useEffect(() => {
    let cancelled = false;

    const fetchOptions = async () => {
      setLoadingOptions(true);
      try {
        const [typesRes, protocolsRes, equipmentRes, tierRes] =
          await Promise.all([
            apiRef.current.get("/instrument-types/"),
            apiRef.current.get("/communication-types/"),
            plantId
              ? apiRef.current.get(`/equipment/?plant=${plantId}`)
              : Promise.resolve({ data: [] }),
            assetId
              ? apiRef.current.get(`/tiers/?relatedTo=${assetId}`)
              : Promise.resolve({ data: [] }),
          ]);

        if (cancelled) return;

        const norm = (res) => res.data?.results ?? res.data ?? [];
        const allEquipment = norm(equipmentRes);

        setInstrumentTypes(norm(typesRes)); // ✅ fixed
        setCommProtocols(norm(protocolsRes));
        setPlcOptions(
          allEquipment.filter(
            (eq) =>
              eq.tagNumber?.toUpperCase().includes("AS") ||
              eq.name?.toUpperCase().includes("AS"),
          ),
        );
        setPidOptions(allEquipment);
        setIoPanelOptions(norm(tierRes));
      } catch (err) {
        if (cancelled) return;
        toast({
          title: "Warning",
          description: "Could not load all dropdown options.",
          status: "warning",
          duration: 4000,
          isClosable: true,
          position: "bottom",
        });
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    };

    fetchOptions();
    return () => {
      cancelled = true;
    };
  }, [plantId, assetId]); // ← open removed, fires once on mount

  const createInstrumentType = async (name) => {
    try {
      const response = await api.post("/instrument-types/", { name });
      const newType = response.data;

      setInstrumentTypes((prev) => [...prev, newType]);

      return newType;
    } catch (error) {
      console.error("Error creating instrument type:", error);
      return null;
    }
  };

  const createCommunicationProtocol = async (name) => {
    try {
      const response = await api.post("/communication-types/", { name });
      const newProtocol = response.data;

      setCommProtocols((prev) => [...prev, newProtocol]);

      return newProtocol;
    } catch (error) {
      console.error("Error creating communication protocol:", error);
      return null;
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setFileList([]);
  };

  const getInitialValues = () => ({
    tagNumber: "",
    instrumentType: "",
    communicationProtocol: "",
    description: "",
    plc: "",
    pid: "",
    ioPanel: "",
    card: "",
  });

  const handlePreview = async (file) => {
    if (!file.url && !file.preview)
      file.preview = await getBase64(file.originFileObj);
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const payload = {
        tagNumber: values.tagNumber,
        description: values.description || "",
        instrumentType: values.instrumentType || null,
        communicationProtocol: values.communicationProtocol || null,
        plc: values.plc || null,
        pid: values.pid || null,
        ioPanel: values.ioPanel || null,
        card: values.card || "",
        channel: values.channel || "",
        // FK fields from route params
        site: siteId ? parseInt(siteId) : null,
        plant: plantId ? parseInt(plantId) : null,
        equipment: assetId ? parseInt(assetId) : null,
        tier: subAssetId ? parseInt(subAssetId) : null,
        component: componentId ? parseInt(componentId) : null,
        // "Parent Assets"-style cross-links
        relatedEquipment: relatedEquipment.map((item) => item.id),
        relatedTiers: relatedTiers.map((item) => item.id),
        relatedComponents: relatedComponents.map((item) => item.id),
      };

      const { data: newInstrument } = await api.post("/instruments/", payload);

      // Upload images if any
      const imageUploads = fileList
        .filter((f) => f.originFileObj)
        .map((f) => {
          const formData = new FormData();
          formData.append("image", f.originFileObj);
          formData.append("instrument", newInstrument.id);
          return api.post("/intrument-images/", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        });

      await Promise.all(imageUploads);

      toast({
        title: "Success",
        description: "Instrument added successfully!",
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
      resetForm();
      reloadComponents?.();
      handleClose();
    } catch (error) {
      const detail = error?.response?.data
        ? JSON.stringify(error.response.data)
        : "Failed to add instrument. Please try again.";
      toast({
        title: "Error",
        description: detail,
        status: "error",
        duration: 6000,
        isClosable: true,
        position: "bottom",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px" } };

  const { canUploadMore, flatMaxCount, limitLabel } = getImageUploadLimitState(
    fileList.length,
    MAX_GALLERY_IMAGES,
  );

  return (
    <Box sx={{ width: "100%", display: "grid", gap: 2 }}>
      <Typography
        onClick={handleOpen}
        variant="link"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: "pointer",
        }}
      >
        <Tooltip title="Add new instrument" placement="right">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SensorOccupied
              fontSize="small"
              style={{ color: "primary.main" }}
            />
            New Instrument
          </Box>
        </Tooltip>
      </Typography>

      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", md: 680 },
            bgcolor: "background.paper",
            boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
            borderRadius: "20px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            maxHeight: "90vh",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              py: 2.5,
              bgcolor: "primary.main",
              flexShrink: 0,
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "#fff", letterSpacing: 0.3 }}
            >
              New Instrument
            </Typography>
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{
                color: "#fff",
                "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
              }}
            >
              <CloseOutlined />
            </IconButton>
          </Box>

          {/* Scrollable body */}
          <Box
            sx={{
              overflowY: "auto",
              flexGrow: 1,
              px: 3,
              py: 2.5,
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "grey.400",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-track": { backgroundColor: "grey.100" },
            }}
          >
            {loadingOptions ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <Formik
                initialValues={getInitialValues()}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting, errors, touched, values, setFieldValue }) => (
                  <Form>
                    {/* ── Identification ── */}
                    <SectionLabel>Identification</SectionLabel>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Field
                          as={TextField}
                          name="tagNumber"
                          label="Tag Number *"
                          fullWidth
                          error={touched.tagNumber && Boolean(errors.tagNumber)}
                          helperText={<ErrorMessage name="tagNumber" />}
                          sx={fieldSx}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Autocomplete
                          options={instrumentTypes}
                          getOptionLabel={(option) => option.name || ""}
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                          value={
                            instrumentTypes.find(
                              (t) => t.id === values.instrumentType,
                            ) || null
                          }
                          filterOptions={(options, params) => {
                            const filtered = options.filter((o) =>
                              o.name
                                .toLowerCase()
                                .includes(params.inputValue.toLowerCase()),
                            );

                            if (
                              params.inputValue.trim() !== "" &&
                              filtered.length === 0
                            ) {
                              filtered.push({
                                id: "__new__",
                                name: `Add "${params.inputValue.trim()}"`,
                                inputValue: params.inputValue.trim(),
                              });
                            }

                            return filtered;
                          }}
                          onChange={async (_, selected) => {
                            if (!selected) {
                              setFieldValue("instrumentType", "");
                              return;
                            }

                            if (selected.id === "__new__") {
                              const created = await createInstrumentType(
                                selected.inputValue,
                              );

                              if (created) {
                                setFieldValue("instrumentType", created.id);
                              }
                            } else {
                              setFieldValue("instrumentType", selected.id);
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Instrument Type *"
                              fullWidth
                              error={
                                touched.instrumentType &&
                                Boolean(errors.instrumentType)
                              }
                              helperText={
                                touched.instrumentType && errors.instrumentType
                              }
                              sx={fieldSx}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>

                    {/* ── Related Assets ── */}
                    <SectionLabel>Related Assets</SectionLabel>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12}>
                        <EquipmentAutocomplete
                          multiple
                          plantId={plantId}
                          value={relatedEquipment}
                          onChange={setRelatedEquipment}
                          label="Related Equipment"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TierAutocomplete
                          multiple
                          plantId={plantId}
                          value={relatedTiers}
                          onChange={setRelatedTiers}
                          label="Related Tiers"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <PanelComponentAutocomplete
                          multiple
                          plantId={plantId}
                          value={relatedComponents}
                          onChange={setRelatedComponents}
                          label="Related Components"
                        />
                      </Grid>
                    </Grid>

                    {/* ── IO Mapping ── */}
                    <SectionLabel>IO Mapping</SectionLabel>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {/* IO Panel — Tier filtered by assetId (equipment) */}
                      <Grid item xs={6}>
                        <Autocomplete
                          options={ioPanelOptions}
                          getOptionLabel={(option) =>
                            option.name ?? option.tagNumber ?? String(option.id)
                          }
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                          value={
                            ioPanelOptions.find(
                              (t) => t.id === values.ioPanel,
                            ) || null
                          }
                          onChange={(_, selected) =>
                            setFieldValue(
                              "ioPanel",
                              selected ? selected.id : "",
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="IO Panel"
                              fullWidth
                              sx={fieldSx}
                            />
                          )}
                        />
                      </Grid>
                      {/* Card — plain text */}
                      <Grid item xs={6}>
                        <Field
                          as={TextField}
                          name="card"
                          label="Card"
                          fullWidth
                          sx={fieldSx}
                        />
                      </Grid>
                      {/* channel — plain text */}
                      <Grid item xs={6}>
                        <Field
                          as={TextField}
                          name="channel"
                          label="Channel"
                          fullWidth
                          sx={fieldSx}
                        />
                      </Grid>
                      {/* PLC — Equipment filtered by plant + "AS" */}
                      <Grid item xs={6}>
                        <Autocomplete
                          options={plcOptions}
                          getOptionLabel={(option) =>
                            option.name ??
                            option.tagNumber ??
                            option.tag ??
                            String(option.id)
                          }
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                          value={
                            plcOptions.find((eq) => eq.id === values.plc) ||
                            null
                          }
                          onChange={(_, selected) =>
                            setFieldValue("plc", selected ? selected.id : "")
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="PLC"
                              fullWidth
                              sx={fieldSx}
                            />
                          )}
                        />
                      </Grid>

                      {/* P&ID — Equipment filtered by plant */}
                      <Grid item xs={6}>
                        <Autocomplete
                          options={pidOptions}
                          getOptionLabel={(option) =>
                            option.name ??
                            option.tagNumber ??
                            option.tag ??
                            String(option.id)
                          }
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                          value={
                            pidOptions.find((eq) => eq.id === values.pid) ||
                            null
                          }
                          onChange={(_, selected) =>
                            setFieldValue("pid", selected ? selected.id : "")
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="P&ID"
                              fullWidth
                              sx={fieldSx}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>

                    {/* ── Communication ── */}
                    <SectionLabel>Communication</SectionLabel>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12}>
                        <Autocomplete
                          options={commProtocols}
                          getOptionLabel={(option) => option.name || ""}
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                          value={
                            commProtocols.find(
                              (p) => p.id === values.communicationProtocol,
                            ) || null
                          }
                          filterOptions={(options, params) => {
                            const filtered = options.filter((o) =>
                              o.name
                                .toLowerCase()
                                .includes(params.inputValue.toLowerCase()),
                            );

                            if (
                              params.inputValue.trim() !== "" &&
                              filtered.length === 0
                            ) {
                              filtered.push({
                                id: "__new__",
                                name: `Add "${params.inputValue.trim()}"`,
                                inputValue: params.inputValue.trim(),
                              });
                            }

                            return filtered;
                          }}
                          onChange={async (_, selected) => {
                            if (!selected) {
                              setFieldValue("communicationProtocol", "");
                              return;
                            }

                            if (selected.id === "__new__") {
                              const created = await createCommunicationProtocol(
                                selected.inputValue,
                              );

                              if (created) {
                                setFieldValue(
                                  "communicationProtocol",
                                  created.id,
                                );
                              }
                            } else {
                              setFieldValue(
                                "communicationProtocol",
                                selected.id,
                              );
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Communication Protocol"
                              fullWidth
                              sx={fieldSx}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          name="description"
                          label="Description"
                          fullWidth
                          multiline
                          rows={3}
                          sx={fieldSx}
                        />
                      </Grid>
                    </Grid>

                    {/* ── Images ── */}
                    <SectionLabel>Images</SectionLabel>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: "12px",
                        border: "1px solid",
                        borderColor: "divider",
                        bgcolor: "grey.50",
                        mb: 3,
                      }}
                    >
                      <Upload
                        listType="picture-card"
                        fileList={fileList}
                        onPreview={handlePreview}
                        // Stops auto-upload (images submit with the whole
                        // form later) AND rejects anything past the
                        // gallery limit.
                        beforeUpload={createLimitedBeforeUpload({
                          getCurrentCount: () => fileList.length,
                          limit: MAX_GALLERY_IMAGES,
                          onLimitExceeded: () =>
                            toast({
                              title: "Limit reached",
                              description: `You can upload up to ${MAX_GALLERY_IMAGES} images.`,
                              status: "warning",
                              duration: 4000,
                              isClosable: true,
                              position: "bottom",
                            }),
                          acceptResult: false,
                        })}
                        onChange={({ fileList: newList }) =>
                          setFileList(newList)
                        }
                        maxCount={flatMaxCount}
                        multiple
                      >
                        {canUploadMore ? (
                          <button
                            style={{ border: 0, background: "none" }}
                            type="button"
                          >
                            <PlusOutlined />
                            <div style={{ marginTop: 8 }}>
                              Upload{limitLabel && <div>{limitLabel}</div>}
                            </div>
                          </button>
                        ) : null}
                      </Upload>
                      {previewImage && (
                        <Image
                          wrapperStyle={{ display: "none" }}
                          preview={{
                            visible: previewOpen,
                            zIndex: 2000,
                            onVisibleChange: (v) => setPreviewOpen(v),
                            afterOpenChange: (v) => !v && setPreviewImage(""),
                          }}
                          src={previewImage}
                        />
                      )}
                    </Box>

                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={isSubmitting}
                      sx={{
                        bgcolor: "primary.main",
                        borderRadius: "10px",
                        textTransform: "none",
                        fontWeight: 600,
                        py: 1.25,
                        "&:hover": { bgcolor: "primary.dark" },
                      }}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={22} color="inherit" />
                      ) : (
                        "Add Instrument"
                      )}
                    </Button>
                  </Form>
                )}
              </Formik>
            )}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

/** Reusable section label matching the existing style */
const SectionLabel = ({ children }) => (
  <Typography
    variant="subtitle2"
    sx={{
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.8,
      color: "primary.main",
      mb: 1.5,
    }}
  >
    {children}
  </Typography>
);

export default NewInstrument;
